import {
  APIClient, APIError, type APIFees, type APIHistoryItem, type Address, HDWallet, InconsistentServerData,
  InvalidMnemonic, type SendPlan, SendFailure, type WalletSnapshot, WalletSync, checkMnemonic, planSend,
} from '../core';
import { formatCW } from './format';
import { Prefs, stringsFor } from './prefs';
import { type OpenVault, type PendingFaceID, Vault } from './vault';

export interface Contact {
  id: string;
  name: string;
  address: string;
}

/** The app locks after this much inactivity (or time away). */
export const LOCK_AFTER_MS = 5 * 60_000;

export type Phase = 'launching' | 'onboarding' | 'locked' | 'ready';

/** Pending authentication request shown by the passcode sheet. */
export interface AuthRequest {
  reason: string;
  resolve: (ok: boolean) => void;
}

/** App-wide state (web equivalent of the iOS AppModel). */
export class AppModel extends EventTarget {
  phase: Phase = 'launching';
  snapshot: WalletSnapshot | null = null;
  errorMessage: string | null = null;
  olderHistory: APIHistoryItem[] = [];
  historyCursor: string | null = null;
  isLoadingHistory = false;
  serverHost = '';
  contacts: Contact[] = [];
  auth: AuthRequest | null = null;
  /** Phrase of a wallet being created, until it is backed up and a passcode chosen. */
  pendingPhrase: string | null = null;
  pendingKind: 'create' | 'restore' | null = null;
  /** The pending new phrase passed the backup check (creation only). */
  pendingBackedUp = false;
  /** Passcode kept in memory for a few seconds to offer Face ID right after setup. */
  faceIDOfferPin: string | null = null;

  readonly api = new APIClient();
  private wallet: HDWallet | null = null;
  private vault: OpenVault | null = null;
  private refreshTask: Promise<void> | null = null;
  private refreshAgain = false;
  private failures = 0;

  private changed() {
    this.dispatchEvent(new Event('change'));
  }

  get s() {
    return stringsFor(Prefs.currentLanguage());
  }

  /** Every transaction loaded so far, newest first. */
  get history(): APIHistoryItem[] {
    const first = this.snapshot?.history ?? [];
    const seen = new Set(first.map((h) => h.txid));
    return [...first, ...this.olderHistory.filter((h) => !seen.has(h.txid))];
  }

  async start() {
    if (!Vault.exists()) {
      Vault.endSession();
      this.phase = 'onboarding';
      this.changed();
      return;
    }
    // Reopened within a few minutes (iPhone reloads Home Screen apps): no Face ID again.
    const v = await Vault.resume(LOCK_AFTER_MS).catch(() => null);
    if (v) {
      this.vault = v;
      try {
        await this.openWallet(await v.phrase());
        return;
      } catch {
        this.vault = null;
        Vault.endSession();
      }
    }
    this.phase = 'locked';
    this.changed();
  }

  /** Called on user activity: keeps the short session alive. */
  touch() {
    if (this.phase === 'ready') Vault.touchSession();
  }

  // ---- Creating / restoring ----

  /** Step 1 of creation: a new phrase, kept in memory until backed up and protected. */
  beginCreate(phrase: string) {
    this.pendingPhrase = phrase;
    this.pendingKind = 'create';
    this.pendingBackedUp = false;
    this.changed();
  }

  confirmBackup() {
    this.pendingBackedUp = true;
    this.changed();
  }

  cancelCreate() {
    this.pendingPhrase = null;
    this.pendingKind = null;
    this.pendingBackedUp = false;
    this.changed();
  }

  /** Validates a typed phrase (throws InvalidMnemonic) and keeps it until a passcode is chosen. */
  beginRestore(phrase: string) {
    this.pendingPhrase = checkMnemonic(phrase);
    this.pendingKind = 'restore';
    this.changed();
  }

  /** Last step of create/restore: encrypts the phrase with the passcode and opens the wallet. */
  async finishSetup(pin: string, restored: boolean) {
    const phrase = this.pendingPhrase;
    if (!phrase) throw new Error('no phrase');
    Prefs.resetWalletData();
    Prefs.setBackupDone(true); // created: verified by the backup quiz; restored: the user typed it
    void restored;
    this.vault = await Vault.create(phrase, pin);
    if (await Vault.faceIDSupported()) this.faceIDOfferPin = pin;
    this.pendingPhrase = null;
    this.pendingKind = null;
    this.pendingBackedUp = false;
    this.contacts = [];
    await this.openWallet(phrase);
  }

  declineFaceIDOffer() {
    this.faceIDOfferPin = null;
    this.changed();
  }

  private async openWallet(phrase: string) {
    this.wallet = await HDWallet.fromMnemonic(phrase);
    this.vault?.remember();
    this.contacts = (await this.vault?.contacts<Contact[]>()) ?? [];
    this.phase = 'ready';
    this.changed();
    void this.refresh();
  }

  // ---- Lock ----

  async unlockWithPin(pin: string): Promise<boolean> {
    const v = await Vault.unlockWithPin(pin);
    if (!v) return false;
    this.vault = v;
    await this.openWallet(await v.phrase());
    return true;
  }

  async unlockWithFaceID(): Promise<boolean> {
    const v = await Vault.unlockWithFaceID();
    if (!v) return false;
    this.vault = v;
    await this.openWallet(await v.phrase());
    return true;
  }

  /** Locks the app: keys are dropped from memory until the next unlock. */
  lock() {
    if (this.phase !== 'ready') return;
    Vault.endSession();
    this.wallet?.wipe();
    this.wallet = null;
    this.vault = null;
    this.auth?.resolve(false);
    this.auth = null;
    this.phase = 'locked';
    this.changed();
  }

  /**
   * Asks for Face ID (when enabled, called directly from the tap so the
   * browser shows the prompt) or the passcode. Resolves true when confirmed.
   */
  authenticate(reason: string): Promise<boolean> {
    if (Vault.hasFaceID()) {
      return Vault.unlockWithFaceID().then((v) => (v ? true : this.askPin(reason)));
    }
    return this.askPin(reason);
  }

  private askPin(reason: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.auth?.resolve(false);
      this.auth = {
        reason,
        resolve: (ok) => {
          this.auth = null;
          this.changed();
          resolve(ok);
        },
      };
      this.changed();
    });
  }

  // ---- Refresh ----

  /**
   * Reloads balance, coins and history, silently (no spinner): the screen just
   * updates. Calls made during a refresh are merged into one more pass.
   */
  refresh(): Promise<void> {
    if (!this.wallet) return Promise.resolve();
    if (this.refreshTask) {
      this.refreshAgain = true;
      return this.refreshTask;
    }
    this.refreshTask = (async () => {
      do {
        this.refreshAgain = false;
        await this.refreshOnce();
      } while (this.refreshAgain && this.wallet);
    })().finally(() => {
      this.refreshTask = null;
    });
    return this.refreshTask;
  }

  private async refreshOnce() {
    const wallet = this.wallet;
    if (!wallet) return;
    try {
      const snap = await new WalletSync(wallet, this.api).refresh();
      if (wallet !== this.wallet) return; // locked meanwhile
      this.failures = 0;
      this.serverHost = this.api.pool.currentHost;
      this.processIncoming(snap.history);
      this.snapshot = snap;
      if (this.olderHistory.length === 0) this.historyCursor = snap.historyCursor;
      this.errorMessage = null;
    } catch (e) {
      this.failures++;
      if (!this.snapshot || this.failures >= 2) this.errorMessage = this.describe(e);
    }
    this.changed();
  }

  async loadMoreHistory() {
    const snap = this.snapshot;
    const cursor = this.historyCursor;
    if (!snap || !cursor || this.isLoadingHistory) return;
    this.isLoadingHistory = true;
    this.changed();
    try {
      const page = await this.api.history(snap.usedAddresses.slice(-200).map((w) => w.address.string), cursor);
      this.olderHistory = [...this.olderHistory, ...page.txs];
      this.historyCursor = page.next_cursor;
    } catch (e) {
      this.errorMessage = this.describe(e);
    }
    this.isLoadingHistory = false;
    this.changed();
  }

  // ---- Incoming payment notifications ----

  private processIncoming(history: APIHistoryItem[]) {
    const seenList = Prefs.seenTxids();
    const seen = new Set(seenList ?? []);
    const fresh = history.filter((h) => !seen.has(h.txid));
    if (fresh.length === 0) return;
    if (seenList !== null && Prefs.notifications()) {
      for (const h of [...fresh].reverse()) if (h.net > 0) this.notify(h);
    }
    Prefs.setSeenTxids([...fresh.map((h) => h.txid), ...(seenList ?? [])]);
  }

  private notify(h: APIHistoryItem) {
    const s = this.s;
    const title = s.notifReceivedTitle(formatCW(h.net));
    const body = h.height === null ? s.notifPendingBody : s.notifConfirmedBody;
    try {
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      navigator.serviceWorker?.ready
        .then((reg) => reg.showNotification(title, { body, icon: '/icons/icon-192.png', tag: 'incoming-' + h.txid }))
        .catch(() => new Notification(title, { body }));
    } catch {}
  }

  // ---- Sending ----

  fees(): Promise<APIFees> {
    return this.api.fees();
  }

  prepareSend(to: Address, amount: number | null, feeRate: number, subtractFee = false): SendPlan {
    const snap = this.snapshot;
    if (!this.wallet || !snap) throw new SendFailure({ kind: 'insufficientFunds', available: 0, needed: 0 });
    return planSend(snap.coins, to, amount, feeRate, this.wallet.changeAddress(snap.nextChangeIndex).scriptPubKey, snap.nextChangeIndex, subtractFee);
  }

  isOwnAddress(a: Address): boolean {
    const snap = this.snapshot;
    if (!snap) return false;
    return a.string === snap.receiveAddress.address.string || snap.usedAddresses.some((w) => w.address.string === a.string);
  }

  /** Confirms (Face ID / passcode), signs in the browser and relays. Returns the txid, or null if cancelled. */
  async send(plan: SendPlan): Promise<string | null> {
    if (!this.wallet) return null;
    if (!(await this.authenticate(this.s.authSend(formatCW(plan.amount))))) return null;
    const tx = this.wallet.sign(plan);
    const txid = await this.api.broadcast(tx);
    void this.refresh();
    return txid;
  }

  // ---- Secrets ----

  async revealPhrase(): Promise<string | null> {
    if (!this.vault || !(await this.authenticate(this.s.authShowPhrase))) return null;
    return this.vault.phrase();
  }

  async exportForCore(): Promise<string[] | null> {
    if (!this.wallet || !(await this.authenticate(this.s.exportCoreTitle))) return null;
    // 1) an empty wallet that can hold private keys; 2) import both chains, with a full rescan.
    const req = [0, 1].map((chain) => ({
      desc: this.wallet!.privateDescriptor(chain),
      active: true,
      internal: chain === 1,
      timestamp: 0,
    }));
    return ['createwallet "coinwow-app" false true', `importdescriptors '${JSON.stringify(req)}'`];
  }

  async forgetWallet(skipAuth = false): Promise<boolean> {
    if (!skipAuth && !(await this.authenticate(this.s.authDeleteWallet))) return false;
    Vault.destroy();
    Prefs.resetWalletData();
    this.wallet?.wipe();
    this.wallet = null;
    this.vault = null;
    this.snapshot = null;
    this.olderHistory = [];
    this.historyCursor = null;
    this.contacts = [];
    this.errorMessage = null;
    this.phase = 'onboarding';
    this.changed();
    return true;
  }

  // ---- Face ID ----

  async finishFaceID(pin: string, p: PendingFaceID): Promise<boolean> {
    const ok = await Vault.finishFaceID(pin, p);
    this.changed();
    return ok;
  }

  disableFaceID() {
    Vault.disableFaceID();
    this.changed();
  }

  // ---- Contacts (encrypted with the wallet) ----

  contactFor(address: string): Contact | undefined {
    return this.contacts.find((c) => c.address === address);
  }

  async saveContact(c: Contact) {
    const i = this.contacts.findIndex((x) => x.id === c.id);
    this.contacts = i >= 0 ? this.contacts.map((x, j) => (j === i ? c : x)) : [...this.contacts, c];
    await this.vault?.saveContacts(this.contacts);
    this.changed();
  }

  async deleteContact(c: Contact) {
    this.contacts = this.contacts.filter((x) => x.id !== c.id);
    await this.vault?.saveContacts(this.contacts);
    this.changed();
  }

  // ---- Errors ----

  describe(e: unknown): string {
    const s = this.s;
    if (e instanceof APIError) {
      const r = e.reason;
      switch (r.kind) {
        case 'rejected': return s.txRejected(r.message);
        case 'rateLimited': return s.rateLimited;
        case 'invalidAddress': return s.addressInvalid;
        case 'server': return s.serverError(r.status);
        case 'invalidResponse': return s.invalidResponse;
        case 'offline': return s.offline(r.detail);
      }
    }
    if (e instanceof SendFailure) {
      const r = e.reason;
      switch (r.kind) {
        case 'invalidAmount': return s.amountInvalid;
        case 'amountBelowDust': return s.amountBelowDust(formatCW(r.minimum));
        case 'insufficientFunds': return s.insufficientFunds(formatCW(r.needed), formatCW(r.available));
        case 'feeTooHigh': return s.feeTooHigh(formatCW(r.fee));
      }
    }
    if (e instanceof InconsistentServerData) return s.inconsistentData;
    if (e instanceof InvalidMnemonic) {
      const r = e.reason;
      if (r.kind === 'unknownWord') return s.restoreUnknownWord(r.word);
      if (r.kind === 'invalidChecksum') return s.restoreBadChecksum;
      return s.restoreBadCount(r.count);
    }
    return s.genericError;
  }
}

export const model = new AppModel();
